import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccountBalanceWalletOutlined,
  AddRounded,
  ArrowForwardRounded,
  AssessmentOutlined,
  InsightsRounded,
  Inventory2Outlined,
  LocalShippingOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchOperationalSummary } from '../api/reports';

const currency = (value) =>
  new Intl.NumberFormat('es-SV', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

function MetricCard({
  title,
  value,
  description,
  action,
  onClick,
  loading,
  unavailable,
  icon: Icon,
  tone = 'primary',
}) {
  const theme = useTheme();
  const color = theme.palette[tone] || theme.palette.primary;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
          transform: { sm: 'translateY(-3px)' },
          boxShadow: `0 14px 32px ${alpha(theme.palette.common.black, 0.1)}`,
          borderColor: alpha(color.main, 0.45),
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.25, md: 2.75 }, '&:last-child': { pb: { xs: 2.25, md: 2.75 } } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={132} height={54} sx={{ mt: 0.25 }} />
            ) : (
              <Typography variant="h4" sx={{ mt: 0.25, fontWeight: 800, letterSpacing: -0.8 }}>
                {unavailable ? '—' : value}
              </Typography>
            )}
          </Box>
          <Avatar
            variant="rounded"
            sx={{
              width: 46,
              height: 46,
              borderRadius: 3,
              color: color.main,
              bgcolor: alpha(color.main, theme.palette.mode === 'dark' ? 0.2 : 0.12),
            }}
          >
            <Icon />
          </Avatar>
        </Stack>

        {loading ? (
          <Box sx={{ mt: 1.75 }}>
            <Skeleton width="95%" />
            <Skeleton width="64%" />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ minHeight: 42, mt: 1.4, lineHeight: 1.55 }}>
            {unavailable ? 'No hay datos disponibles en este momento.' : description}
          </Typography>
        )}

        <Button
          size="small"
          color={tone}
          endIcon={<ArrowForwardRounded fontSize="small" />}
          onClick={onClick}
          disabled={loading}
          sx={{ mt: 1.15, ml: -0.5, fontWeight: 700 }}
        >
          {action}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    fetchOperationalSummary()
      .then((data) => {
        if (active) setSummary(data);
      })
      .catch(() => {
        if (active) setError('No fue posible cargar el resumen operativo.');
      });

    return () => {
      active = false;
    };
  }, []);

  const loading = !summary && !error;
  const unavailable = Boolean(error);

  const metrics = [
    {
      title: 'Por cobrar',
      value: currency(summary?.por_cobrar),
      description: `${Number(summary?.cobros_pendientes || 0)} venta(s) pendientes de pago`,
      action: 'Revisar ventas',
      onClick: () => navigate('/ventas'),
      icon: AccountBalanceWalletOutlined,
      tone: 'warning',
    },
    {
      title: 'Envíos pendientes',
      value: Number(summary?.envios_pendientes || 0),
      description: `${Number(summary?.envios_enviados || 0)} venta(s) ya enviadas por confirmar`,
      action: 'Gestionar envíos',
      onClick: () => navigate('/ventas'),
      icon: LocalShippingOutlined,
      tone: 'info',
    },
    {
      title: 'Stock bajo',
      value: Number(summary?.productos_bajo_stock ?? summary?.productos_stock_bajo ?? 0),
      description: `Productos con ${Number(summary?.umbral_stock_bajo || 2)} unidades o menos`,
      action: 'Ver productos',
      onClick: () => navigate('/productos'),
      icon: Inventory2Outlined,
      tone: 'error',
    },
    {
      title: 'Ventas activas',
      value: Number(summary?.ventas_activas || 0),
      description: 'Ventas que continúan en proceso',
      action: 'Abrir ventas',
      onClick: () => navigate('/ventas'),
      icon: TrendingUpOutlined,
      tone: 'success',
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 } }}>
      <Paper
        elevation={0}
        sx={(theme) => ({
          position: 'relative',
          overflow: 'hidden',
          mb: { xs: 2.5, md: 3 },
          p: { xs: 2.5, sm: 3, md: 4 },
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.36 : 0.2),
          background: `linear-gradient(118deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.25 : 0.12)} 0%, ${alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.14 : 0.07)} 100%)`,
          '&::after': {
            content: '""',
            position: 'absolute',
            width: { xs: 180, md: 280 },
            height: { xs: 180, md: 280 },
            right: { xs: -75, md: 32 },
            top: { xs: -92, md: -116 },
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.09),
            pointerEvents: 'none',
          },
        })}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={3}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box sx={{ maxWidth: 650 }}>
            <Chip
              icon={<InsightsRounded fontSize="small" />}
              label="Centro de control"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mb: 1.5, fontWeight: 700 }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: -1.15 }}>
              Resumen operativo
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 570, fontSize: { sm: '1.025rem' } }}>
              Prioridades de venta, cobranza, envío e inventario para mantener la operación al día.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<AssessmentOutlined />}
              onClick={() => navigate('/reportes')}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Ver reportes
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => navigate('/ventas/nuevo')}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Nueva venta
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error} Puedes seguir navegando y volver a intentarlo más tarde.
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 1.75 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Prioridades del día
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
            Consulta primero lo que requiere una acción de tu parte.
          </Typography>
        </Box>
        <Chip
          label={loading ? 'Actualizando datos' : 'Vista operativa'}
          size="small"
          variant="outlined"
          color={loading ? 'primary' : 'default'}
          sx={{ fontWeight: 700 }}
        />
      </Stack>

      <Grid container spacing={{ xs: 1.5, md: 2.25 }}>
        {metrics.map((metric) => (
          <Grid item xs={12} sm={6} lg={3} key={metric.title}>
            <MetricCard {...metric} loading={loading} unavailable={unavailable} />
          </Grid>
        ))}
      </Grid>

      <Paper
        elevation={0}
        sx={{
          mt: { xs: 2.5, md: 3 },
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 1.75 },
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Atajos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Accede rápidamente a las tareas más frecuentes.
            </Typography>
          </Box>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
            <Button size="small" onClick={() => navigate('/ventas')}>
              Ventas
            </Button>
            <Button size="small" onClick={() => navigate('/productos')}>
              Productos
            </Button>
            <Button size="small" onClick={() => navigate('/clientes')}>
              Clientes
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
}
